import { ReactNode, useState } from "react";
import styles from "./Alert.module.scss";

import InfoIcon from "../../assets/images/icons/ui/states/alert-light-blue-icon.svg";
import ErrorIcon from "../../assets/images/icons/ui/states/alert-icon.svg";
import WarnIcon from "../../assets/images/icons/ui/states/warn-icon.svg";
import ChevronDown from "../../assets/images/icons/ui/arrows/collapsable-down.svg";
import ChevronUp from "../../assets/images/icons/ui/arrows/collapsable-up.svg";
import CloseIcon from "../../assets/images/icons/close-icon-dark.svg?react";
import { AnimatePresence, motion } from "framer-motion";

export interface AlertProps {
  /**
   * The content to display in the button
   */
  children: string | ReactNode;
  /**
   * The alert appearance style
   */
  variant: "INFO" | "ERROR" | "WARN";
  /**
   * Custom styles
   */
  className?: string;
  /**
   * A summary of the children is shown initially and all of it once clicked.
   */
  hasSummary?: boolean;
  /**
   * Can be dismissed by clicking an icon that is added at the end of it's content.
   */
  dismissable?: boolean;
}

const Alert = ({
  children,
  variant,
  className,
  hasSummary,
  dismissable,
}: AlertProps) => {
  const {
    container,
    iconStyle,
    infoBackground,
    warnBackground,
    errorBackground,
    clickable,
    summary,
  } = styles;

  const alertVariants = {
    INFO: {
      icon: <img src={InfoIcon} className={iconStyle} />,
      background: infoBackground,
    },
    WARN: {
      icon: <img src={WarnIcon} className={iconStyle} />,
      background: warnBackground,
    },
    ERROR: {
      icon: <img src={ErrorIcon} className={iconStyle} />,
      background: errorBackground,
    },
  };

  const { icon, background } = alertVariants[variant];

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  return (
    <AnimatePresence>
      {!isDismissed && (
        <motion.div
          className={` ${container} ${background} ${hasSummary && clickable} ${
            className ?? ""
          }`}
          onClick={() => setIsDetailsOpen(!isDetailsOpen)}
          onTransitionEnd={() => setIsDismissed(false)}
          exit={{ opacity: 0 }}
        >
          {icon}

          <div className="flex w-full justify-between items-center gap-4">
            <p className={`${hasSummary && !isDetailsOpen ? summary : ""}`}>
              {children}
            </p>

            {hasSummary && (
              <div>
                {isDetailsOpen ? (
                  <img src={ChevronUp} alt="" />
                ) : (
                  <img src={ChevronDown} alt="" />
                )}
              </div>
            )}

            {dismissable && (
              <CloseIcon
                className="cursor-pointer hover:brightness-200"
                onClick={() => setIsDismissed(true)}
              />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Alert;
